import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Grid,
  Avatar,
  TextField,
  MenuItem,
  Drawer,
  InputAdornment,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const AddIntervenantDrawer = ({
  open,
  onClose,
  intervenantData,
  setIntervenantData,
  intervenantPreview,
  onFileChange,
  onSubmit,
  isSubmitting,
}) => {
  const setField = (field) => (e) =>
    setIntervenantData({ ...intervenantData, [field]: e.target.value });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 3000 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, p: 3 } }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
          Nouvel Intervenant / Praticien
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)', pr: 1 }}>
        <Stack spacing={2.5}>
          {/* Section Photo */}
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Avatar
              src={intervenantPreview}
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 1,
                border: '2px dashed #4318FF',
              }}
            />
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="intervenant-photo-upload"
              type="file"
              onChange={onFileChange}
            />
            <label htmlFor="intervenant-photo-upload">
              <Button variant="outlined" component="span" size="small" startIcon={<PhotoCamera />}>
                Photo de profil
              </Button>
            </label>
          </Box>

          {/* Identité */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Nom"
              fullWidth
              required
              value={intervenantData.nom_user}
              onChange={setField('nom_user')}
            />
            <TextField
              label="Prénom"
              fullWidth
              required
              value={intervenantData.prenom_user}
              onChange={setField('prenom_user')}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={intervenantData.email_user}
              onChange={setField('email_user')}
            />
            <TextField
              label="Mot de passe"
              type="password"
              fullWidth
              required
              value={intervenantData.password_user}
              onChange={setField('password_user')}
            />
          </Stack>

          {/* Titre */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Titre (ex: Coach Senior)"
              fullWidth
              value={intervenantData.title_user}
              onChange={setField('title_user')}
            />
          </Stack>

          {/* Boutique et Commission Globale */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Lien Boutique"
              fullWidth
              placeholder="https://..."
              value={intervenantData.lien_boutique}
              onChange={setField('lien_boutique')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StorefrontIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Commission Globale (%)"
              type="number"
              fullWidth
              placeholder="20"
              value={intervenantData.commission || ''}
              onChange={setField('commission')}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Stack>

          {/* 🚀 NOUVELLE SECTION : Paramètres Financiers & Praticien Infos */}
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2B3674' }}>
            Tarifs & Commissions Spécifiques (SOA)
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Prix SOA (€)"
              type="number"
              fullWidth
              placeholder="40.00"
              value={intervenantData.prix_soa || ''}
              onChange={setField('prix_soa')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Com. Consultation (%)"
              type="number"
              fullWidth
              placeholder="0"
              value={intervenantData.commission_consultation || ''}
              onChange={setField('commission_consultation')}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <TextField
              label="Com. Vidéo (%)"
              type="number"
              fullWidth
              placeholder="0"
              value={intervenantData.commission_video || ''}
              onChange={setField('commission_video')}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Stack>
          <Divider sx={{ my: 1 }} />

          {/* Contact et Localisation */}
          <TextField
            label="Téléphone"
            fullWidth
            value={intervenantData.phone_user}
            onChange={setField('phone_user')}
          />

          <TextField
            label="Adresse complète"
            fullWidth
            value={intervenantData.addresse_user}
            onChange={setField('addresse_user')}
          />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Ville"
                fullWidth
                value={intervenantData.ville_user}
                onChange={setField('ville_user')}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField label="CP" fullWidth value={intervenantData.cp_user} onChange={setField('cp_user')} />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Province/Pays"
                fullWidth
                value={intervenantData.province_user}
                onChange={setField('province_user')}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            placeholder="Spécialité: Design, Marketing, Web"
            multiline
            rows={3}
            InputLabelProps={{ shrink: true }}
            value={intervenantData.main_speciality_user}
            onChange={setField('main_speciality_user')}
            helperText="Vous pouvez ajouter plusieurs spécialités en les séparant par une virgule (,)"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '& fieldset': { borderColor: '#E0E5F2' },
              },
              '& .MuiInputLabel-root': { color: '#2B3674', fontWeight: 700 },
              '& .MuiFormHelperText-root': {
                color: '#A3AED0',
                fontSize: '0.8rem',
                marginTop: '8px',
              },
            }}
          />

          {/* Bio et Statut */}
          <TextField
            label="Description / Bio"
            multiline
            rows={3}
            fullWidth
            value={intervenantData.description_user}
            onChange={setField('description_user')}
          />

          <TextField
            select
            label="État du compte"
            fullWidth
            value={intervenantData.etat_user}
            onChange={setField('etat_user')}
          >
            <MenuItem value="active">Actif (Accès autorisé)</MenuItem>
            <MenuItem value="inactive">Inactif (Accès bloqué)</MenuItem>
          </TextField>

          <Button
            fullWidth
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: '12px',
              bgcolor: '#4318FF',
              fontWeight: 700,
              mt: 2,
              '&:hover': { bgcolor: '#3311CC' },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Créer l'Intervenant"}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default AddIntervenantDrawer;